// src/services/sqfAiService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const base = `${API_URL}/api/sqf`;

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ─── File upload to S3 via presigned URL (reuses document upload infrastructure) ──
export const uploadFileForAi = async (file) => {
  // 1. Get presigned URL from the existing documents endpoint
  const { uploadUrl, publicUrl } = await axios
    .post(`${API_URL}/api/documents/upload-url`, {
      fileName: file.name,
      contentType: file.type,
      fileSize: file.size,
    }, auth())
    .then(r => r.data);

  // 2. PUT directly to S3
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': file.type },
  });

  return { fileUrl: publicUrl, mimeType: file.type };
};

// AI import/generate/translate calls can run well past Heroku's 30s router ceiling on a real
// document — SSE keeps the connection alive for as long as the call actually takes, instead of
// failing with a misleading browser CORS/ERR_FAILED error once the router gives up. Mirrors
// services/maintenanceService.js's/knowledgeBaseService.js's own sseStream() helper (duplicated
// rather than shared, same convention every SSE-consuming service file in this app follows).
function sseStream(url, body, onProgress) {
  return new Promise(async (resolve, reject) => {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
    } catch (e) { return reject(e); }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Request failed' }));
      return reject(err);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, '').trim();
          if (!line || line.startsWith(':')) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === 'progress') onProgress?.(ev.message);
            else if (ev.type === 'done')  return resolve(ev);
            else if (ev.type === 'error') return reject(new Error(ev.message));
          } catch {}
        }
      }
    } catch (e) { reject(e); }
  });
}

// ─── AI endpoints ─────────────────────────────────────────────────────────────

export const importChecklist = (payload, onProgress) =>
  sseStream(`${base}/ai/import-checklist`, payload, onProgress);

export const importPolicy = (payload, onProgress) =>
  sseStream(`${base}/ai/import-policy`, payload, onProgress);

export const generatePolicy = (payload, onProgress) =>
  sseStream(`${base}/ai/generate-policy`, payload, onProgress);

export const translateEntity = (payload, onProgress) =>
  sseStream(`${base}/ai/translate`, payload, onProgress);

export const importClauses = (payload) =>
  axios.post(`${base}/ai/import-clauses`, payload, auth()).then(r => r.data);

const sqfAiService = {
  uploadFileForAi,
  importChecklist,
  importPolicy,
  generatePolicy,
  translateEntity,
  importClauses,
};

export default sqfAiService;
